import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import { GiftCertificateUsageEntity } from 'Models/Entities';
import { FetchTransactionPricing } from 'Services/Api/_HumanWritten/PricingService/PricingService';
import { removeGiftCertificateFromTransaction } from 'Services/Api/_HumanWritten/GiftCertificateService';
import { fetchGiftCertificateUsagesByTransactionId } from 'Util/_HumanWritten/FetchGiftCertificates';
import { formatCurrency } from 'Util/_HumanWritten/CurrencyUtils';
import alertToast from 'Util/ToastifyUtils';
import { LottieSpinner } from 'Views/Components/_HumanWritten/Lottie/LottieSpinner';
import RemoveLineItemButton from 'Views/Components/_HumanWritten/LineItems/RemoveLineItemButton';
import PurchasePromoGiftCard from 'Views/Components/_HumanWritten/Payment/PurchasePromoGiftCard';

export interface PurchaseReviewLine {
	label: string;
	value: string;
}

export interface PurchaseReviewBodyProps {
	transactionId: string;
	title?: string;
	/** Purchase details shown above the price breakdown (product, dates, registration, …). */
	detailLines?: PurchaseReviewLine[];
	inCheckIn?: boolean;
	/** Notified whenever the applied credits change, for callers mirroring them into their own store. */
	onUsagesChange?: (usages: GiftCertificateUsageEntity[]) => void;
}

/**
 * The review step shared by the anytime-ticket (#2613) and parking (#2614) purchase modals: what is
 * being bought, the price breakdown, and the voucher input.
 *
 * <p>The voucher is applied BEFORE the payment step mounts, so Elements mounts once with the final
 * figure. This used to be load-bearing: <c>StripeProvider</c> keyed its <c>&lt;Elements&gt;</c> tree
 * on the total, so a credit applied on the payment step remounted the whole payment form and
 * destroyed anything the customer had typed into the Payment Element. That key is gone (see
 * StripeProvider) and a reprice now flows through <c>elements.update()</c>, so this ordering is a
 * simplicity choice rather than a workaround — the price is settled before it is asked for.</p>
 */
function PurchaseReviewBody({
	transactionId,
	title,
	detailLines,
	inCheckIn = false,
	onUsagesChange,
}: PurchaseReviewBodyProps) {
	const [usages, setUsages] = useState<GiftCertificateUsageEntity[]>([]);
	const [netTotal, setNetTotal] = useState<number | null>(null);
	const [loading, setLoading] = useState(true);
	const [removingUsageId, setRemovingUsageId] = useState<string | null>(null);
	// Set when the price could not be fetched. Without it a failed quote cleared the spinner and
	// rendered a $0.00 subtotal and total, which reads like a free purchase rather than an error.
	const [priceFailed, setPriceFailed] = useState(false);

	const appliedUsages = usages.filter(usage => (usage.usedValue ?? 0) > 0);
	const creditApplied = appliedUsages.reduce((sum, usage) => sum + (usage.usedValue ?? 0), 0);
	// The server's total is already net of every applied credit, so add them back for the pre-credit
	// subtotal rather than re-deriving it from a product or quote price — which would miss any
	// per-user-type override or parking rate the transaction was actually created with.
	const subtotal = (netTotal ?? 0) + creditApplied;

	const refresh = useCallback(async () => {
		if (!transactionId) {
			return;
		}
		setLoading(true);
		try {
			const [pricing, fetched] = await Promise.all([
				// Card fee excluded: the surcharge depends on the payment method, which is not chosen
				// until the next step, so including it here would quote a fee that may never apply.
				FetchTransactionPricing(transactionId, false, inCheckIn),
				fetchGiftCertificateUsagesByTransactionId(transactionId),
			]);
			setNetTotal(pricing?.total ?? 0);
			const nextUsages = fetched.data ?? [];
			setUsages(nextUsages);
			setPriceFailed(false);
			if (onUsagesChange) {
				onUsagesChange(nextUsages);
			}
		} catch (e) {
			// Show the failure rather than a $0.00 breakdown, and leave the previous figures in place
			// so a failed re-price after an apply doesn't blank a price the customer had already seen.
			console.error(e);
			setPriceFailed(true);
		} finally {
			setLoading(false);
		}
	}, [transactionId, inCheckIn, onUsagesChange]);

	useEffect(() => {
		refresh();
	}, [refresh]);

	const onRemoveUsage = async (usageId: string) => {
		setRemovingUsageId(usageId);
		try {
			await removeGiftCertificateFromTransaction(transactionId, usageId, false, inCheckIn);
			await refresh();
		} catch (e) {
			console.error(e);
			alertToast('Could not remove that credit. Please try again.', 'error');
		} finally {
			setRemovingUsageId(null);
		}
	};

	if (!transactionId) {
		return (
			<div className="purchase-review-step">
				<p className="purchase-review-step__error">
					<FormattedMessage
						id="purchase-review__transaction-lost__message"
						defaultMessage="This purchase is no longer available. Please go back and start again."
					/>
				</p>
			</div>
		);
	}

	return (
		<div className="purchase-review-step">
			<h4 className="purchase-review-step__title">
				{title ?? <FormattedMessage id="purchase-review__title" defaultMessage="Review your purchase" />}
			</h4>

			{!!detailLines?.length && (
				<dl className="purchase-review-step__details">
					{detailLines.map(line => (
						<div className="purchase-review-step__detail" key={line.label}>
							<dt className="purchase-review-step__detail-label">{line.label}</dt>
							<dd className="purchase-review-step__detail-value">{line.value}</dd>
						</div>
					))}
				</dl>
			)}

			<div className="purchase-review-step__summary">
				{loading ? (
					<div className="purchase-review-step__loading">
						<LottieSpinner />
					</div>
				) : (
					<>
						{priceFailed && (
							<p className="purchase-review-step__error">
								<FormattedMessage
									id="purchase-review__price-failed__message"
									defaultMessage="We couldn't load the price for this purchase. Please go back and try again."
								/>
							</p>
						)}

						{/* Only once a price has actually been quoted — otherwise the breakdown would
						    read $0.00 across the board and look like a free purchase. */}
						{netTotal !== null && (
							<>
								<div className="purchase-review-step__row">
									<span className="purchase-review-step__row-label">
										<FormattedMessage
											id="purchase-review__subtotal__label"
											defaultMessage="Subtotal"
										/>
									</span>
									<span className="purchase-review-step__row-value">{formatCurrency(subtotal)}</span>
								</div>

								{appliedUsages.map(usage => (
									<div
										className="purchase-review-step__row purchase-review-step__row--credit"
										key={usage.id}
									>
										<span className="purchase-review-step__row-label">
											<FormattedMessage
												id="purchase-review__credit-code__label"
												defaultMessage="Credit ({code})"
												values={{ code: usage.giftCertificate?.code?.toUpperCase() ?? '' }}
											/>
											<RemoveLineItemButton
												className="purchase-review-step__remove"
												disabled={removingUsageId === usage.id}
												onClick={() => onRemoveUsage(usage.id)}
												ariaLabel={`Remove credit ${usage.giftCertificate?.code?.toUpperCase() ?? ''}`.trim()}
											/>
										</span>
										<span className="purchase-review-step__row-value">
											-{formatCurrency(usage.usedValue ?? 0)}
										</span>
									</div>
								))}

								<div className="purchase-review-step__row purchase-review-step__row--total">
									<span className="purchase-review-step__row-label">
										<FormattedMessage id="purchase-review__total__label" defaultMessage="Total" />
									</span>
									<span className="purchase-review-step__row-value">
										{/* Never render a negative total: the server caps each usage at the transaction
								    price, so this only guards a stale price arriving before the re-priced one. */}
										{formatCurrency(Math.max(0, subtotal - creditApplied))}
									</span>
								</div>
							</>
						)}
					</>
				)}
			</div>

			<div className="purchase-review-step__voucher">
				<p className="purchase-review-step__voucher-label">
					<FormattedMessage
						id="purchase-review__voucher-prompt__label"
						defaultMessage="Have a voucher or credit?"
					/>
				</p>
				<PurchasePromoGiftCard transactionId={transactionId} forCheckIn={inCheckIn} onApplied={refresh} />
			</div>
		</div>
	);
}

export default observer(PurchaseReviewBody);
